import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentRef } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { globalObservability } from '@opendoor/observability/slim';
import { IconHeartFilled } from '@tabler/icons-react';
import { buildPropertyPhotoUrl } from 'components/marketplace/property/helpers';
import { useMediaQuery } from 'components/shared/hooks/useMediaQuery';
import Router from 'next/router';
import { Marker, Popup } from 'react-map-gl/mapbox';

import type { MapMarkerProps } from './types';

import styles from './MapMarker.module.css';

function formatNumber(number: number) {
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: number >= 1_000_000 ? 2 : 0,
    notation: 'compact',
    compactDisplay: 'short',
  });
}

const HOVER_INTENT_MS = 120;

// Dedupe RUM impressions so panning does not fire a new `mapMarkerHover` every
// time a popup opens. The Set is cleared on SPA route changes so navigating
// between markets (/homes/a -> /homes/b) re-fires impressions for repeated
// addresses that appear in the new market's results.
const markerImpressionsFired = new Set<string>();
let routerListenerAttached = false;

function ensureRouterListener() {
  if (routerListenerAttached || typeof window === 'undefined') {
    return;
  }
  routerListenerAttached = true;
  Router.events.on('routeChangeStart', () => {
    markerImpressionsFired.clear();
  });
}

// Test-only reset. The dedupe Set persists for the SPA session, so unit tests
// that render MapMarker multiple times need a way to reset it without relying
// on salted address tokens that mask bugs in the dedupe contract itself.
export const __resetMarkerImpressionsForTest = () => {
  markerImpressionsFired.clear();
};

function trackMarkerImpression(addressToken: string) {
  if (!addressToken || markerImpressionsFired.has(addressToken)) {
    return;
  }
  ensureRouterListener();
  markerImpressionsFired.add(addressToken);
  try {
    globalObservability.getDatadogRumClient?.()?.addAction?.('marketplace.mapMarkerHover', {
      addressToken,
    });
  } catch {
    // analytics must never break the UI
  }
}

function buildPhotoSlots(photos: readonly string[] | null | undefined) {
  const [primary, secondary, tertiary] = photos ?? [];
  return {
    primary: buildPropertyPhotoUrl(primary, { preset: '3:2-sm' }),
    secondary: buildPropertyPhotoUrl(secondary, { preset: '3:2-xs' }),
    tertiary: buildPropertyPhotoUrl(tertiary, { preset: '3:2-xs' }),
  };
}

function buildMarkerAriaLabel(property: MapMarkerProps['property']) {
  const parts: string[] = [];
  if (property.listPrice != null) {
    parts.push(`Property listed at $${property.listPrice.toLocaleString()}`);
  } else {
    parts.push('Property');
  }
  const beds = property.beds != null ? `${property.beds} beds` : null;
  const baths = property.baths != null ? `${property.baths} baths` : null;
  const summary = [beds, baths].filter(Boolean).join(', ');
  if (summary) {
    parts.push(summary);
  }
  if (property.isLiked) {
    parts.push('favorited');
  }
  return parts.join(', ');
}

type PopupImageProps = {
  uri: string | null;
  alt: string;
  className: string;
};

const PopupImage = ({ uri, alt, className }: PopupImageProps) => {
  const [errored, setErrored] = useState(false);

  if (!uri || errored) {
    return <Box className={cn('bg-od-bg-secondary', className)} />;
  }

  return (
    <Image
      alt={alt}
      src={uri}
      onError={() => setErrored(true)}
      className={cn('object-cover', className)}
    />
  );
};

type PopupBodyProps = {
  property: MapMarkerProps['property'];
};

// Not wrapped in `memo`: callers pass a fresh `property` object on every
// MapMarker render, so shallow-prop comparison would always miss. The popup
// body only mounts while `isActive && isDesktop`, so re-renders are cheap
// enough to skip the optimization rather than add a custom comparator.
const PopupBody = ({ property }: PopupBodyProps) => {
  const slots = buildPhotoSlots(property.photos);

  return (
    <Col className={styles.popupEnter}>
      <PopupImage
        uri={slots.primary}
        alt="map-marker-primary-property-image"
        className="aspect-[7/3] w-88 rounded-t-2xl"
      />
      <Row className="justify-between pt-1.5">
        <PopupImage
          uri={slots.secondary}
          alt="map-marker-secondary-property-image"
          className="aspect-[43/25] w-43"
        />
        <PopupImage
          uri={slots.tertiary}
          alt="map-marker-tertiary-property-image"
          className="aspect-[43/25] w-43"
        />
      </Row>
      <Col className="gap-1.5 px-5 pb-4 pt-5">
        <Row className="items-baseline justify-between">
          {property.listPrice != null ? (
            <Text as="p" className="text-label-xl text-od-content-primary">
              {`$${property.listPrice.toLocaleString()}`}
            </Text>
          ) : null}
          {property.isLiked ? (
            <IconHeartFilled size={14} className="self-center text-od-content-error" />
          ) : null}
        </Row>
        {(() => {
          const stats = [
            property.beds != null ? `${property.beds} bds` : null,
            property.baths != null ? `${property.baths} ba` : null,
            property.livingSquareFootage != null
              ? `${property.livingSquareFootage.toLocaleString()} sqft`
              : null,
          ].filter((part): part is string => part !== null);
          if (stats.length === 0) {
            return null;
          }
          return (
            <Row className="items-baseline">
              {stats.map((part, index) => (
                <Fragment key={part}>
                  {index > 0 ? (
                    <Text as="span" className="text-body-sm text-od-content-inactive-secondary">
                      {' • '}
                    </Text>
                  ) : null}
                  <Text as="span" className="text-body-sm text-od-content-secondary">
                    {part}
                  </Text>
                </Fragment>
              ))}
            </Row>
          );
        })()}
        {property.street ? (
          <Text as="p" className="truncate text-body-sm text-od-content-tertiary">
            {property.street}
          </Text>
        ) : null}
      </Col>
    </Col>
  );
};

export const MapMarker = ({
  property,
  onClick,
  moveToTop,
  setFocused: focusTopMarker,
  isSelected,
  isLoading,
}: MapMarkerProps) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [isHovering, setIsHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isActive = isHovering || isFocused || Boolean(isSelected) || Boolean(isLoading);
  const pressableRef = useRef<HTMLElement | null>(null);
  // Stable ref callback so the marker isn't detached/re-attached every render.
  const setPressableRef = useCallback((node: ComponentRef<typeof Row> | null) => {
    pressableRef.current = node as HTMLElement | null;
  }, []);
  const hoverTimerRef = useRef<number | null>(null);
  // Keyed by the preloaded href so the effect re-fires when the primary photo
  // URL changes. A simple boolean guard would block all subsequent preloads.
  const preloadedHrefRef = useRef<string | null>(null);
  // Mirror isFocused in a ref so the hover-intent timer callback always reads
  // the latest value instead of the closure captured at schedule time.
  const isFocusedRef = useRef(isFocused);
  useEffect(() => {
    isFocusedRef.current = isFocused;
  }, [isFocused]);
  const popupId = `cosmos-map-marker-popup-${property.addressToken}`;

  useEffect(() => {
    // Fire impressions regardless of viewport: focus / selection / loading can
    // all activate a marker on mobile (via tap-select) and those count as
    // engagement even when the desktop-only hover popup does not render.
    if (isActive) {
      trackMarkerImpression(property.addressToken);
    }
  }, [isActive, property.addressToken]);

  useEffect(() => {
    if (!isActive || !isDesktop) {
      return;
    }
    const primary = property.photos?.[0];
    if (!primary) {
      return;
    }
    const href = buildPropertyPhotoUrl(primary, { preset: '3:2-sm' });
    if (!href) {
      return;
    }
    if (preloadedHrefRef.current === href) {
      return;
    }
    preloadedHrefRef.current = href;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    link.fetchPriority = 'high';
    document.head.appendChild(link);
    return () => {
      link.parentNode?.removeChild(link);
    };
  }, [isActive, isDesktop, property.photos]);

  useEffect(() => {
    if (focusTopMarker) {
      pressableRef.current?.focus();
    }
  }, [focusTopMarker]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsFocused(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [isFocused]);

  const cancelHoverTimer = useCallback(() => {
    if (hoverTimerRef.current != null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => cancelHoverTimer();
  }, [cancelHoverTimer]);

  const handleOnHover = () => {
    cancelHoverTimer();
    hoverTimerRef.current = window.setTimeout(() => {
      setIsHovering(true);
      moveToTop?.(isFocusedRef.current);
    }, HOVER_INTENT_MS);
  };

  const handleOnMouseOut = () => {
    cancelHoverTimer();
    setIsHovering(false);
  };

  const handleOnFocus = () => {
    moveToTop?.(true);
    setIsFocused(true);
  };

  const handleOnBlur = () => {
    setIsFocused(false);
  };

  const handleOnClick = () => {
    cancelHoverTimer();
    setIsHovering(false);
    setIsFocused(false);
    onClick();
  };

  const ariaLabel = buildMarkerAriaLabel(property);

  return (
    <Marker
      key={`map-marker-${property.addressToken}`}
      latitude={property.location?.lat}
      longitude={property.location?.lon}
    >
      <Row
        aria-label={ariaLabel}
        aria-expanded={isActive && isDesktop}
        aria-describedby={isActive && isDesktop ? popupId : undefined}
        analyticsName="property-marker-click"
        tabIndex={0}
        onMouseEnter={handleOnHover}
        onMouseLeave={handleOnMouseOut}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onClick={handleOnClick}
        ref={setPressableRef}
        className={cn(
          'z-[1] h-7 items-center justify-center rounded-full',
          property.isLiked ? 'w-19' : 'w-15',
          isActive ? 'bg-od-bg-inverse-primary' : 'bg-od-bg-primary',
        )}
      >
        {isLoading ? (
          <Text as="span" className="text-label-sm text-od-content-inverse-primary">
            •••
          </Text>
        ) : (
          <>
            {property.isLiked ? (
              <IconHeartFilled size={14} className="pr-1 text-od-content-error" />
            ) : null}
            {property.listPrice != null ? (
              <Text
                as="span"
                className={cn(
                  'text-label-sm',
                  isActive ? 'text-od-content-inverse-primary' : 'text-od-content-primary',
                )}
              >
                {formatNumber(property.listPrice)}
              </Text>
            ) : null}
          </>
        )}
      </Row>
      {isActive && isDesktop ? (
        <Popup
          longitude={property.location?.lon}
          latitude={property.location?.lat}
          anchor="bottom"
          offset={20}
          closeButton={false}
        >
          <Box id={popupId} role="region">
            <PopupBody property={property} />
          </Box>
        </Popup>
      ) : null}
    </Marker>
  );
};
